import React from 'react'
import gsap from 'gsap'

export default function Loader({ onDone }) {
  const [progress, setProgress] = React.useState(0)
  const [canSkip, setCanSkip] = React.useState(false)
  const rootRef = React.useRef(null)

  React.useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })
    tl.fromTo(root.querySelector('[data-title]'), { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 })
      .fromTo(root.querySelectorAll('[data-line]'), { scaleX: 0 }, { scaleX: 1, duration: 0.6, stagger: 0.07 }, '-=0.2')
      .fromTo(root.querySelector('[data-sub]'), { y: 8, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 }, '-=0.35')

    // Fake load progression for a premium first-impression.
    const start = performance.now()
    let raf = 0
    function tick(now) {
      const t = Math.min((now - start) / 1600, 1)
      // Ease curve that slows near the end.
      const eased = 1 - Math.pow(1 - t, 3)
      const target = Math.floor(eased * 100)
      setProgress((p) => Math.max(p, target))
      if (t < 1) {
        raf = requestAnimationFrame(tick)
      } else {
        setCanSkip(true)
        // Small hold for polish
        setTimeout(() => {
          gsap.to(root, { opacity: 0, duration: 0.5, onComplete: onDone })
        }, 350)
      }
    }
    raf = requestAnimationFrame(tick)

    return () => {
      tl.kill()
      cancelAnimationFrame(raf)
    }
  }, [onDone])

  return (
    <div ref={rootRef} className="fixed inset-0 z-[60] bg-bg">
      <div className="absolute inset-0 noise opacity-80" />
      <div className="absolute inset-0 grid-bg opacity-[0.18]" />

      <div className="relative h-full w-full flex items-center justify-center">
        <div className="w-[min(900px,92vw)]">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="text-xs tracking-[0.35em] text-white/60">BOOTSTRAPPING CLIENT</div>
              <div data-title className="mt-2 text-3xl md:text-5xl font-semibold tracking-tight">DeltaReloaded</div>
              <div data-sub className="mt-3 text-white/60 max-w-xl">Initializing renderer, auth layer, and patch delivery endpoints.</div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-semibold tabular-nums">{String(progress).padStart(3, '0')}%</div>
              <div className="text-xs tracking-[0.28em] text-white/50">LOADING</div>
            </div>
          </div>

          <div className="mt-8 rounded-2xl overflow-hidden glow bg-panel/60">
            <div className="p-5 md:p-7">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/70">System checks</div>
                <div className="text-sm text-white/50">{progress < 100 ? 'Running' : 'Complete'}</div>
              </div>

              <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
                <div style={{ width: `${progress}%` }} className="h-full bg-white/70 transition-[width] duration-200" />
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  ['Anti-cheat hooks', progress > 25],
                  ['Matchmaking relay', progress > 55],
                  ['Launcher delivery', progress > 80]
                ].map(([label, on]) => (
                  <div key={label} className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">{label}</div>
                      <div className={`text-xs ${on ? 'text-white/70' : 'text-white/35'}`}>{on ? 'OK' : '...'}</div>
                    </div>
                    <div className="mt-3 flex gap-1">
                      {[0,1,2,3,4,5,6,7,8,9].map((i) => (
                        <div
                          key={i}
                          data-line
                          className={`h-1 flex-1 origin-left ${on ? 'bg-white/55' : 'bg-white/10'}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-xs text-white/50">Tip: Append <span className="text-white/70">?forceLoader=1</span> to preview this sequence.</div>
                <button
                  type="button"
                  disabled={!canSkip}
                  onClick={() => onDone()}
                  className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-xs disabled:opacity-40 hover:bg-white/15"
                >
                  {canSkip ? 'Continue' : 'Please wait'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 text-xs text-white/35 tracking-[0.28em]">DELTA // RELOADED // BUILD: WEB-01</div>
        </div>
      </div>
    </div>
  )
}
